import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Sparkles, ChevronRight, Trees, Flower2, Activity } from 'lucide-react';
import HeroWebGL from './HeroWebGL';

// Import Slideshow Images (Optimized WebP formats)
import heroTiger from '../assets/hero_tiger.webp';
import heroCulture from '../assets/hero_culture.webp';
import heroFood from '../assets/hero_food.webp';
import heroBeach from '../assets/hero_beach.webp';
import heroLandscape from '../assets/hero_landscape.webp';
import heroDance from '../assets/hero_dance.webp';
import heroYoga from '../assets/hero_yoga.webp';

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {

  const heroRef = useRef(null);
  const textContainerRef = useRef(null);
  const jewelTagRef = useRef(null);
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const slides = [
    { image: heroTiger, alt: "Majestic Bengal Tiger", position: "center" },
    { image: heroCulture, alt: "Traditional Indian Musicians", position: "center" },
    { image: heroFood, alt: "Exquisite Indian Cuisine", position: "center" },
    { image: heroBeach, alt: "Serene Indian Beach", position: "center" },
    { image: heroLandscape, alt: "Lush Indian Fields", position: "center" },
    { image: heroDance, alt: "Classical Indian Dancer", position: "center 20%" },
    { image: heroYoga, alt: "Yoga at Sunrise", position: "center" }
  ];

  // Handle mobile detection and resizing
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Slideshow Timer (Bypassed or slowed down on mobile for battery/CPU)
  useEffect(() => {
    const intervalTime = isMobile ? 6000 : 4500;
    const slideTimer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, intervalTime);

    return () => clearInterval(slideTimer);
  }, [slides.length, isMobile]);

  // GSAP Animations (Entry + ScrollTrigger Parallax)
  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Entry Animations
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      tl.from('.hero-badge', {
        opacity: 0,
        y: -30,
        duration: 1.2,
        delay: 0.2
      })
      .from('.hero-title', {
        opacity: 0,
        y: 40,
        duration: 1.4,
        stagger: 0.15
      }, '-=0.9')
      .from('.hero-desc', {
        opacity: 0,
        y: 30,
        duration: 1.2
      }, '-=1.0')
      .from('.hero-buttons', {
        opacity: 0,
        y: 20,
        duration: 1.0
      }, '-=1.0');

      // Spring intro rotation for the Jewel Tag
      gsap.from(jewelTagRef.current, {
        scale: 0,
        rotate: -360,
        opacity: 0,
        duration: 1.8,
        ease: 'elastic.out(1, 0.75)',
        delay: 0.8
      });

      // Continuous slow shine loop on Jewel Tag
      gsap.to('.jewel-shine', {
        x: '200%',
        duration: 3.5,
        repeat: -1,
        ease: 'linear',
        repeatDelay: 3
      });

      // Continuous float loop on Jewel Tag
      gsap.to(jewelTagRef.current, {
        y: -12,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // Continuous shine loop on Hero Badge
      gsap.to('.badge-shine', {
        x: '200%',
        duration: 2.5,
        repeat: -1,
        ease: 'linear',
        repeatDelay: 2
      });

      // Continuous float loop on Hero Badge
      gsap.to('.hero-badge', {
        y: -6,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // 2. ScrollTrigger Parallax & Fade-out transitions
      // Text container moves up faster and fades out
      gsap.to(textContainerRef.current, {
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        },
        y: -150,
        opacity: 0,
        ease: 'none'
      });

      // Slideshow images container fades out and scales down slightly on scroll
      gsap.to('.slides-container', {
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        },
        scale: 0.95,
        opacity: 0.3,
        ease: 'none'
      });


    }, heroRef); // Scoped to heroRef container

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-screen flex items-center overflow-hidden bg-slate-950"
    >
      {/* Background Image Slideshow with Additive WebGL Overlay */}
      <div className="absolute inset-0 z-0 overflow-hidden slides-container">
        {slides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              idx === currentSlide ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0'
            }`}
            style={{ transitionProperty: 'opacity, transform' }}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover brightness-[0.75] contrast-[1.05]"
              style={{ objectPosition: slide.position }}
            />
          </div>
        ))}

        {/* Dynamic Raycasted WebGL Constellation Overlay */}
        <HeroWebGL />

        {/* Futuristic Gradients & Ambient Shading */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/30 to-transparent z-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-slate-950 to-transparent z-10"></div>
      </div>

      {/* Futuristic Floating Service Quick Access */}
      <div className="fixed top-28 right-4 md:top-32 md:right-8 z-50 w-24 h-36 md:w-32 md:h-44" ref={jewelTagRef}>
        
        {/* Wildlife: Top Right */}
        <div onClick={() => window.open('https://adventure.sunshinemanipal.com', '_blank')} className="absolute top-0 right-0 group flex items-center justify-end cursor-pointer">
          <span className="absolute right-[120%] opacity-0 group-hover:opacity-100 group-hover:-translate-x-1 transition-all duration-300 bg-white text-orange-600 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap border border-orange-100 pointer-events-none">
            Wildlife Safaris
          </span>
          <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_rgba(249,115,22,0.4)] border-4 border-white/20 hover:border-white transition-all overflow-hidden relative">
             <Trees size={24} className="relative z-10 group-hover:scale-110 transition-transform" />
             <div className="absolute inset-0 bg-white/20 -translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full"></div>
          </div>
        </div>

        {/* Yoga: Center Left */}
        <div onClick={() => window.open('https://mysticroutes.sunshinemanipal.com/', '_blank')} className="absolute top-1/2 -translate-y-1/2 left-0 group flex items-center justify-end cursor-pointer">
          <span className="absolute right-[120%] opacity-0 group-hover:opacity-100 group-hover:-translate-x-1 transition-all duration-300 bg-white text-orange-600 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap border border-orange-100 pointer-events-none">
            Yoga & Wellness
          </span>
          <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_rgba(249,115,22,0.4)] border-4 border-white/20 hover:border-white transition-all overflow-hidden relative">
             <Flower2 size={24} className="relative z-10 group-hover:scale-110 transition-transform" />
             <div className="absolute inset-0 bg-white/20 -translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full"></div>
          </div>
        </div>

        {/* Medical: Bottom Right */}
        <div onClick={() => window.open('https://cureconnect.sunshinemanipal.com', '_blank')} className="absolute bottom-0 right-0 group flex items-center justify-end cursor-pointer">
          <span className="absolute right-[120%] opacity-0 group-hover:opacity-100 group-hover:-translate-x-1 transition-all duration-300 bg-white text-orange-600 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap border border-orange-100 pointer-events-none">
            Medical Tourism
          </span>
          <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_rgba(249,115,22,0.4)] border-4 border-white/20 hover:border-white transition-all overflow-hidden relative">
             <Activity size={24} className="relative z-10 group-hover:scale-110 transition-transform" />
             <div className="absolute inset-0 bg-white/20 -translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Main Content Info Container */}
      <div className="container mx-auto px-6 relative z-30 max-w-6xl pt-20">
        <div ref={textContainerRef} className="text-left space-y-6 md:space-y-8">
          <div className="hero-badge inline-block">
            <div className="relative overflow-hidden inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500 text-white text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] shadow-[0_0_15px_rgba(249,115,22,0.4)]">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -translate-x-[250%] badge-shine skew-x-[-20deg] z-0 pointer-events-none"></div>
              <Sparkles size={12} className="animate-pulse text-yellow-200 relative z-10" /> 
              <span className="relative z-10">Celebrating 25 Years</span>
            </div>
          </div>

          <div className="hero-title max-w-4xl">
            <h1 className="text-3xl md:text-6xl font-serif font-bold text-white leading-[1.15] tracking-wide">
              Experience extraordinary <br />
              journeys with the warmth of <br />
              <span className="text-orange-500 italic">Indian Hospitality.</span>
            </h1>
          </div>

          <div className="hero-desc max-w-2xl border-l-4 border-orange-500 pl-6">
            <p className="text-base md:text-lg text-slate-300 font-medium leading-relaxed">
              Authentic spiritual experiences, beautifully curated. Providing hassle-free vacations since 2001.
            </p>
          </div>

          <div className="hero-buttons flex flex-wrap gap-4 pt-4">
            <Link
              to="/contact"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full text-sm font-black flex items-center gap-2 transition-all shadow-lg hover:shadow-orange-500/20 uppercase tracking-widest"
            >
              Plan Your Trip <ChevronRight size={16} />
            </Link>
            <Link
              to="/destinations"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-full text-sm font-black transition-all uppercase tracking-widest"
            >
              Explore Destinations
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
